import arrayMutators from "final-form-arrays";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Form } from "react-final-form";
import { is_empty } from "utils/validations";
import { INITIAL_DATA_TYPE } from "../constants/AddCustomer.constants";
import AddCustomerFormContent from "../modules/AddCustomerFormContent/AddCustomerFormContent";
import { useAddCustomer } from "../utils/useAddCustomer";
import {
  getInitialValuesOnType,
  sanitizeInitialValues,
} from "../utils/AddCustomer.utils";

/**
 * AddCustomerModal component for adding new bookings / customer entries in CRM or via custom submit handlers.
 *
 * Prop Sanitization:
 * - `initialValuesProp` is sanitized via `sanitizeInitialValues` (which filters keys against `ALLOWED_INITIAL_FORM_KEYS`)
 *   to prevent unknown or unexpected caller keys from leaking into form state and downstream handlers.
 *
 * Submit Handler Semantics:
 * - When `onSubmit` prop is provided (e.g. in QR Payments workflow), it overrides `handleAddBooking` to execute custom submission logic,
 *   bypassing standard CRM API calls while remaining subject to form field validation.
 */
const AddCustomerModal = (props) => {
  const {
    onClose,
    listing_data: listingDataProp,
    entity_data: entityDataProp,
    onSuccess,
    initialValues: initialValuesProp,
    onSubmit,
  } = props;

  const { handleAddBooking } = useAddCustomer({ onClose, onSuccess });

  const initialDataType =
    entityDataProp && !is_empty(entityDataProp.uuid)
      ? INITIAL_DATA_TYPE.PLAN
      : INITIAL_DATA_TYPE.LISTING;

  const [initialFormValues] = useState(() => {
    const defaultValues = getInitialValuesOnType(initialDataType, {
      listing_data: listingDataProp,
      entity_data: entityDataProp,
    });

    return {
      ...defaultValues,
      ...sanitizeInitialValues(initialValuesProp),
    };
  });

  const handleFormSubmit =
    typeof onSubmit === "function" ? onSubmit : handleAddBooking;

  return (
    <Form
      initialValues={initialFormValues}
      onSubmit={handleFormSubmit}
      mutators={{
        ...arrayMutators,
      }}
    >
      {({ handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit}>
            <AddCustomerFormContent
              {...props}
              handleSubmit={handleSubmit}
              initialDataType={initialDataType}
            />
          </form>
        );
      }}
    </Form>
  );
};

AddCustomerModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  onSubmit: PropTypes.func,
  listing_data: PropTypes.object,
  entity_data: PropTypes.object,
  listing_type: PropTypes.any,
  showOfferingSelector: PropTypes.bool,
  disableOffering: PropTypes.bool,
  disableAmount: PropTypes.bool,
  requireSlotTimeValidation: PropTypes.bool,
  title: PropTypes.string,
  primaryBtnText: PropTypes.string,
  initialValues: PropTypes.shape({
    full_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    customer_phone_number: PropTypes.string,
    country_code: PropTypes.string,
    country: PropTypes.string,
    listing_uid: PropTypes.string,
    entity_uuid: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    send_comms: PropTypes.bool,
    send_installment_comms: PropTypes.bool,
    selected_quantity: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    selected_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currency: PropTypes.string,
    selected_appointment_slots: PropTypes.array,
    appointment_slots: PropTypes.array,
    selected_batch: PropTypes.string,
    available_currencies: PropTypes.array,
    payment_type: PropTypes.string,
    address_details: PropTypes.string,
    customer_timezone: PropTypes.string,
    booking_start_date: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    membership_uuid: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    offering_variant_uuid: PropTypes.string,
    installments: PropTypes.array,
    note: PropTypes.string,
    payment_mode_uuid: PropTypes.string,
    gateway_transaction_id: PropTypes.string,
    customer_gst_details: PropTypes.object,
    gst_number: PropTypes.string,
    gst_name: PropTypes.string,
    billing_state: PropTypes.string,
    tag_uuids: PropTypes.array,
    tag_name: PropTypes.string,
    payment_mode_name: PropTypes.string,
  }),
};

export default AddCustomerModal;
